import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import InnerHTML from '@Components/InnerHTML';
import isUndefined from 'lodash/isUndefined';
import isEmpty from 'lodash/isEmpty';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import { citationLiteratureFormatter, patentFamilyFormatter } from '@Services/FormatUtils';
import CitationsButtonsComponent from '@Components/Results/reactions/reaction-details/citations-buttons.component';
import ListOfAuthorsComponent from '@Components/Results/reactions/reaction-details/list-of-authors.component';
import DocumentJournalsComponent from '@Components/Results/documents/document-journals.component';
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';
import DocumentAbstract from '@Components/Results/documents/DocumentAbstract';

import './ReactionCitations.scss';

export const NO_AUTHOR_LABEL = 'No author';
export const AUTHOR_PENDING_LABEL = 'Author information pending';

export function getDocumentNoAuthorLabel(publicationYear) {
  if (!publicationYear) {
    return NO_AUTHOR_LABEL;
  }
  return publicationYear.toString()?.indexOf(new Date().getFullYear()?.toString()) >= 0 ? AUTHOR_PENDING_LABEL : NO_AUTHOR_LABEL;
}

function ReactionCitations({ metadata, citation, index, onFullTextLinkClick, onIssueCitedCounterClick, onAbstractOpen, onAuthorClick }) {
  const [isAbstractExpanded, setIsAbstractExpanded] = useState(false);

  const toggleAbstractExpanded = useCallback((val) => {
    setIsAbstractExpanded((prev) => (!isUndefined(val) ? val : !prev));
  }, []);

  const literatureFormatter = citationLiteratureFormatter;
  const patentFormatter = patentFamilyFormatter;

  const getDefaultCitationTemplate = () => {
    const author = !isEmpty(citation?.citationJournals) ? getDocumentNoAuthorLabel(citation?.year) : '';

    return (
      <>
        {citation.authorKeywords && (
          <div className="content-literature">
            <span className="rx-element-literature">{citation.authorKeywords?.join(', ')}</span>
          </div>
        )}
        <div className="content-authors">
          <span className="rx-element-authors">
            {citation.authors ? (
              <ListOfAuthorsComponent key={citation.id} metadata={metadata} citation={citation} onAuthorClick={onAuthorClick} />
            ) : (
              author
            )}
          </span>
          {!isEmpty(citation.citationJournals) && <DocumentJournalsComponent journals={citation.citationJournals} />}
        </div>
      </>
    );
  };

  const getPatentTemplate = () => {
    const citationPatentsHtml = ` - ${literatureFormatter(citation.citationPatents)}`;

    return [
      <div key="0" className="content-authors">
        <span className="rx-element-assignee">
          {citation.assignees ? (
            <>
              {!Auth.isFeatureEnabled(FEATURES.disablePatentSightUIChanges) && <span>Current Patent Assignee: </span>}
              <InnerHTML tag="span" html={citation.assignees} />
            </>
          ) : (
            getDocumentNoAuthorLabel(citation.year)
          )}
        </span>
        <InnerHTML tag="span" className="rx-element-literature" html={citationPatentsHtml} />
        {!isEmpty(citation.citationJournals) && <DocumentJournalsComponent journals={citation.citationJournals} />}
      </div>,
      <div key="1" className="content-literature">
        {citation.patentFamily && (
          <span className="rx-element-literature">
            <span className="rx-element-literature-title">Patent Family Members:</span>
            <InnerHTML tag="span" html={patentFormatter(citation.patentFamily)} />
          </span>
        )}
      </div>,
      citation.location ? (
        <div key="2" className="content-location-in-patent">
          Location in patent:&nbsp;
          <InnerHTML tag="span" html={citation.location} />
        </div>
      ) : null,
    ];
  };

  const isPatentType = citation.type && citation.type?.indexOf('Patent') !== -1;

  return (
    <div className="citation">
      {/* default citation template */}
      {!isPatentType && !citation.abstractText && getDefaultCitationTemplate()}
      {/* Patent */}
      {isPatentType && getPatentTemplate()}
      <CitationsButtonsComponent
        metadata={metadata}
        citation={citation}
        index={index}
        isAbstractExpanded={isAbstractExpanded}
        toggleAbstractExpanded={toggleAbstractExpanded}
        onFullTextLinkClick={onFullTextLinkClick}
        onIssueCitedCounterClick={onIssueCitedCounterClick}
        onAbstractOpen={onAbstractOpen}
      />

      {/* Abstract */}
      {citation.abstractText && isAbstractExpanded && (
        <DocumentAbstract
          abstractText={citation.abstractText}
          closeClick={() => toggleAbstractExpanded(false)}
          panelClassName={`reaction-citations-abstract-${index}`}
        />
      )}
    </div>
  );
}

ReactionCitations.propTypes = {
  metadata: MetadataPropTypesShape,
  index: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.number.isRequired]),
  citation: PropTypes.object.isRequired,
  onFullTextLinkClick: PropTypes.func,
  onIssueCitedCounterClick: PropTypes.func,
  onAbstractOpen: PropTypes.func,
  onAuthorClick: PropTypes.func,
};

export default ReactionCitations;
